import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RotateCw, AlertTriangle, Mail } from "lucide-react";
import {
  buildEmailConfirmationRedirect,
  clearSignupResume,
  describeAuthLinkError,
  readAuthLinkErrorFromUrl,
  readSignupResume,
  resolveResumeDestination,
} from "@/lib/signup-resume";

export const Route = createFileRoute("/verify-email")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Confirm your email — The Hospitality Reserve" },
      { name: "description", content: "Confirm the email address on your Hospitality Reserve account to activate sign-in and notifications." },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://thehospitalityreserve.com/verify-email" }],
  }),
  component: VerifyEmailPage,
});

// ---- Client-side rate limiter (per-email) ----
const RESEND_COOLDOWN_SEC = 60;        // wait between sends
const RESEND_MAX_PER_HOUR = 5;         // hard cap to prevent abuse
const STORAGE_KEY = "reserve.verify-email.attempts";

type AttemptState = {
  email: string;
  attempts: number[];  // unix ms timestamps in the last hour
  lastSentAt: number | null;
};

function readAttempts(email: string): AttemptState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { email, attempts: [], lastSentAt: null };
    const s = JSON.parse(raw) as AttemptState;
    if (s.email !== email) return { email, attempts: [], lastSentAt: null };
    const cutoff = Date.now() - 60 * 60_000;
    s.attempts = (s.attempts ?? []).filter((t) => t > cutoff);
    return s;
  } catch {
    return { email, attempts: [], lastSentAt: null };
  }
}

function writeAttempts(s: AttemptState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch { /* noop */ }
}

function computeNextResendAt(lastSentAt: number | null, attempts: number[]): number | null {
  const now = Date.now();
  const cooldownEnd = lastSentAt ? lastSentAt + RESEND_COOLDOWN_SEC * 1000 : now;
  if (attempts.length < RESEND_MAX_PER_HOUR) {
    return cooldownEnd > now ? cooldownEnd : null;
  }
  // Hourly cap is reached; next slot opens when the oldest attempt rolls out of the window.
  const oldest = Math.min(...attempts);
  const capReleaseAt = oldest + 60 * 60_000;
  const nextAt = Math.max(cooldownEnd, capReleaseAt);
  return nextAt > now ? nextAt : null;
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function supportMailto(email: string | null): string {
  const subject = encodeURIComponent(`Email confirmation issue${email ? ` for ${email}` : ""}`);
  const body = encodeURIComponent(
    `Hi The Hospitality Reserve support,\n\n` +
    `I'm having trouble confirming my email address.` +
    `${email ? ` My account email is: ${email}\n` : "\n"}` +
    `Please verify me manually.\n\nThanks`
  );
  return `mailto:support@thehospitalityreserve.com?subject=${subject}&body=${body}`;
}


const LAST_EMAIL_KEY = "reserve.verify-email.address";

function VerifyEmailPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string | null>(null);
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [linkError, setLinkError] = useState<ReturnType<typeof describeAuthLinkError>>(null);
  const [destination, setDestination] = useState<string>("/dashboard");

  // Actionable errors handed back by the confirmation link (expired, already
  // used, unknown address) + the exact signup step to return to.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const { code, description } = readAuthLinkErrorFromUrl(window.location.href);
    setLinkError(describeAuthLinkError(code, description));
    setDestination(resolveResumeDestination(params.get("next")));
    const saved = readSignupResume();
    if (saved?.email) setEmail((prev) => prev ?? saved.email);
  }, []);

  // Resolve who we're verifying. If the confirmation link was opened in a
  // different browser (no session), we stay on this page instead of bouncing
  // to the landing page — the visitor can still resend to their address.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (cancelled) return;
      const user = data.user as
        | { email?: string | null; email_confirmed_at?: string | null; confirmed_at?: string | null }
        | null;
      if (user) {
        setSignedIn(true);
        setEmail(user.email ?? null);
        if (user.email) {
          try { localStorage.setItem(LAST_EMAIL_KEY, user.email); } catch { /* noop */ }
        }
        if (user.email_confirmed_at || user.confirmed_at) {
          clearSignupResume();
          window.location.replace(destination);
        }
      } else {
        setSignedIn(false);
        try { setEmail(localStorage.getItem(LAST_EMAIL_KEY)); } catch { /* noop */ }
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, destination]);
  const failureCount = useRef(0);
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const [attemptsLeft, setAttemptsLeft] = useState(RESEND_MAX_PER_HOUR);
  const [lastSentAt, setLastSentAt] = useState<number | null>(null);
  const [nextResendAt, setNextResendAt] = useState<number | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("We auto-check every few seconds and will redirect you once your email is confirmed.");

  // Hydrate rate-limit state from storage
  useEffect(() => {
    if (!email) return;
    const s = readAttempts(email);
    setLastSentAt(s.lastSentAt);
    setAttemptsLeft(Math.max(0, RESEND_MAX_PER_HOUR - s.attempts.length));
    setNextResendAt(computeNextResendAt(s.lastSentAt, s.attempts));
  }, [email]);

  // Cooldown + next-available ticker
  useEffect(() => {
    const tick = () => {
      if (!email) return;
      const s = readAttempts(email);
      const nextAt = computeNextResendAt(s.lastSentAt, s.attempts);
      setNextResendAt(nextAt);
      if (!s.lastSentAt) { setCooldownLeft(0); return; }
      const left = Math.max(0, RESEND_COOLDOWN_SEC - Math.floor((Date.now() - s.lastSentAt) / 1000));
      setCooldownLeft(left);
      setAttemptsLeft(Math.max(0, RESEND_MAX_PER_HOUR - s.attempts.length));
    };
    tick();
    const t = window.setInterval(tick, 1000);
    return () => window.clearInterval(t);
  }, [email]);

  // Shared confirmation check used by polling and manual retry
  const checkConfirmed = useCallback(async (manual = false): Promise<boolean> => {
    if (signedIn === false) {
      if (manual) setStatus("You're signed out on this device. Sign in again to continue where you left off.");
      return false;
    }
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      const u = data.user as
        | { email_confirmed_at?: string | null; confirmed_at?: string | null; phone_confirmed_at?: string | null; }
        | null;
      if (u?.email_confirmed_at || u?.confirmed_at || u?.phone_confirmed_at) {
        setStatus("Confirmed — taking you back to where you left off…");
        clearSignupResume();
        window.location.replace(destination);
        return true;
      }
      failureCount.current = 0;
      setPollError(null);
      if (manual) {
        setStatus("Not confirmed yet. Click the link in your inbox, then press the button again.");
      }
      return false;
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Network error";
      if (manual) {
        setStatus(`Couldn't check right now: ${msg}`);
        setPollError(msg);
      } else {
        failureCount.current += 1;
        if (failureCount.current >= 2) {
          setStatus("We're having trouble reaching the server. We'll keep trying, or you can check manually below.");
          setPollError(msg);
        }
      }
      return false;
    }
  }, [navigate, signedIn, destination]);

  // Auto-poll for confirmation (every 5s + immediate check when tab returns)
  useEffect(() => {
    if (signedIn !== true) return;
    let interval: number | null = null;
    checkConfirmed();
    interval = window.setInterval(() => checkConfirmed(), 5000);

    const onVisible = () => {
      if (document.visibilityState === "visible") checkConfirmed();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      if (interval) window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [checkConfirmed, signedIn]);

  const resend = async () => {
    if (!email) return;
    if (cooldownLeft > 0) {
      toast.info(`Please wait until ${formatTime(Date.now() + cooldownLeft * 1000)} before requesting another email.`);
      return;
    }
    if (attemptsLeft <= 0) {
      toast.error("Hourly resend limit reached. Try again later or contact support.");
      return;
    }
    setSending(true);
    setStatus("Sending a new confirmation email…");
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: {
          emailRedirectTo: buildEmailConfirmationRedirect(window.location.origin, destination),
        },
      });
      if (error) throw error;
      const now = Date.now();
      const prev = readAttempts(email);
      const next: AttemptState = {
        email,
        attempts: [...prev.attempts, now],
        lastSentAt: now,
      };
      writeAttempts(next);
      setLastSentAt(now);
      setAttemptsLeft(Math.max(0, RESEND_MAX_PER_HOUR - next.attempts.length));
      setNextResendAt(computeNextResendAt(now, next.attempts));
      setStatus("New confirmation email sent. Check your inbox (and spam).");
      toast.success("Confirmation email sent.");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Couldn't send the email.";
      setStatus(`Couldn't send: ${msg}`);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const checkNow = async () => {
    setChecking(true);
    setStatus("Checking your inbox confirmation…");
    await checkConfirmed(true);
    setChecking(false);
  };

  const tryAgainNow = async () => {
    setStatus("Retrying now…");
    await checkConfirmed(true);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  };

  const resendDisabled = sending || !email || cooldownLeft > 0 || attemptsLeft <= 0;
  const resendLabel = sending
    ? "Sending…"
    : nextResendAt
      ? `Resend available at ${formatTime(nextResendAt)}`
      : attemptsLeft <= 0
        ? "Hourly limit reached"
        : `Resend the email${attemptsLeft < RESEND_MAX_PER_HOUR ? ` (${attemptsLeft} left)` : ""}`;

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="w-full max-w-md text-center">
        <p className="tracking-wide-luxury text-accent">The Hospitality Reserve</p>
        <h1 className="mt-6 font-display text-3xl text-foreground">Confirm your email</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          We sent a confirmation link to{" "}
          <span className="text-foreground">{email ?? "your inbox"}</span>.
        </p>
        <p className="mt-2 text-sm font-medium text-foreground">
          Be sure to check your junk/spam folder — confirmation emails often land there.
        </p>

        {linkError && (
          <div
            role="alert"
            className="mt-6 border border-destructive/40 bg-destructive/5 p-4 text-left"
          >
            <p className="flex items-center gap-2 font-display text-sm text-destructive">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              {linkError.title}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">{linkError.body}</p>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
              {linkError.kind === "expired" && (
                <>
                  <li>Press “Resend the email” below.</li>
                  <li>Open the new link within 60 minutes, on this device if you can.</li>
                </>
              )}
              {linkError.kind === "used" && (
                <li>Choose “Sign in” below — no new email is needed.</li>
              )}
              {linkError.kind === "mismatch" && (
                <>
                  <li>Check the address below for typos, then resend.</li>
                  <li>If you used a different address, sign in with that one instead.</li>
                </>
              )}
              {linkError.kind === "generic" && (
                <li>Resend below, or email support and we’ll confirm you manually.</li>
              )}
            </ul>
          </div>
        )}
        {signedIn === false && (
          <div className="mt-6 space-y-2 border border-border bg-card p-4 text-left">
            <label htmlFor="verify-email-address" className="text-xs tracking-wide-luxury text-muted-foreground">
              Your sign-up email
            </label>
            <input
              id="verify-email-address"
              type="email"
              value={email ?? ""}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
            <p className="text-[11px] text-muted-foreground">
              You're signed out on this device. Resend the link below, then sign in again — your
              sign-up details are saved to your account.
            </p>
          </div>
        )}

        <div
          role="status"
          aria-live="polite"
          className="mt-6 border border-border bg-card px-4 py-3 text-xs text-foreground/80"
        >
          {status}
        </div>

        <div className="mt-6 flex flex-col gap-3">
          {signedIn !== false && (
          <Button onClick={checkNow} disabled={checking}>
            {checking ? "Checking…" : "I've confirmed — let me in"}
          </Button>
          )}
          {signedIn === false && (
            <Button onClick={() => navigate({ to: "/auth" })}>Sign in</Button>
          )}
          {pollError && (
            <Button variant="secondary" onClick={tryAgainNow} disabled={checking}>
              <RotateCw className="mr-2 h-4 w-4" />
              Try again now
            </Button>
          )}
          <Button variant="outline" onClick={resend} disabled={resendDisabled}>
            {resendLabel}
          </Button>
          {nextResendAt && (
            <p className="text-[11px] text-muted-foreground">
              Next resend available at{" "}
              <span className="tabular-nums text-foreground">{formatTime(nextResendAt)}</span>
              {attemptsLeft <= 0 && " (hourly limit reached)"}
            </p>
          )}
        </div>

        <section
          aria-labelledby="verify-help-heading"
          className="mt-8 border border-border bg-card p-4 text-left"
        >
          <h2
            id="verify-help-heading"
            className="flex items-center gap-2 tracking-wide-luxury text-[10px] uppercase text-muted-foreground"
          >
            <Mail className="h-3.5 w-3.5" aria-hidden="true" />
            Email not arriving?
          </h2>
          <ol className="mt-3 list-decimal space-y-2 pl-5 text-xs text-muted-foreground">
            <li>
              <span className="text-foreground">Check junk, spam and Promotions.</span> First-time
              senders very often land there. Search your mailbox for
              {" "}
              <span className="text-foreground">“The Hospitality Reserve”</span>.
            </li>
            <li>
              <span className="text-foreground">Mark it “Not spam”</span> if you find it there — that
              tells your provider future emails are wanted.
            </li>
            <li>
              <span className="text-foreground">Add our sender to your contacts / safe list:</span>
              {" "}
              <span className="text-foreground">no-reply@notify.thehospitalityreserve.com</span>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                <li>Gmail: open the email → three dots → “Add [sender] to Contacts list”.</li>
                <li>Outlook: Settings → Junk email → Safe senders → add the address.</li>
                <li>Apple Mail / iCloud: tap the sender name → “Add to VIP”.</li>
              </ul>
            </li>
            <li>
              <span className="text-foreground">Still nothing after a few minutes?</span> Resend
              above, then email support and we’ll confirm you manually.
            </li>
          </ol>
        </section>

        <div className="mt-6 flex flex-col items-center gap-2">
          <Button variant="ghost" size="sm" asChild>
            <a href={supportMailto(email)}>
              Email support with my address prefilled
            </a>
          </Button>
          <p className="text-[11px] text-muted-foreground">
            We can verify you manually if the email isn't arriving.
          </p>
        </div>

        <button
          onClick={signOut}
          className="mt-6 text-xs tracking-wide-luxury text-muted-foreground hover:text-foreground"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
